import BaseMarker from "./BaseMarker";
import * as THREE from 'three';
import { markRaw } from 'vue';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

const ZONE_COLOR = 0x66c7b4;
const ZONE_LINE_WIDTH = 3;          // pixels (worldUnits:false) — thicker than the old 1px line
const ZONE_LINE_WIDTH_SELECTED = 5; // emphasised when the zone is selected
const ZONE_FILL_OPACITY = 0.06;          // faint additive "glass wall" shell inside the box
const ZONE_FILL_OPACITY_SELECTED = 0.12;

// Card height as a fraction of the zone box's largest dimension, clamped so it never gets too small
// on tiny zones or dominates huge ones. Sizing the label relative to the box keeps it legible at
// the framed zoom for any zone scale. Width follows the rendered-card aspect.
const LABEL_SIZE_FRACTION = 0.24;
const LABEL_MIN_HEIGHT = 1.3;   // world-unit floor so small zones stay readable
const LABEL_MAX_HEIGHT = 4.0;   // world-unit cap so large zones don't get an oversized card

// mdi 24x24 path data, rendered onto the label canvas via Path2D so the icons stay crisp.
const MARKER_ICON_PATH = 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z';
const HOTSPOT_ICON_PATH = 'M17.66 11.2C17.43 10.9 17.15 10.64 16.89 10.38C16.22 9.78 15.46 9.35 14.82 8.72C13.33 7.26 13 4.85 13.95 3C13 3.23 12.17 3.75 11.46 4.32C8.87 6.4 7.85 10.07 9.07 13.22C9.11 13.32 9.15 13.42 9.15 13.55C9.15 13.77 9 13.97 8.8 14.05C8.57 14.15 8.33 14.09 8.14 13.93C8.08 13.88 8.04 13.83 8 13.76C6.87 12.33 6.69 10.28 7.45 8.64C5.78 10 4.87 12.3 5 14.47C5.06 14.97 5.12 15.47 5.29 15.97C5.43 16.57 5.7 17.17 6 17.7C7.08 19.43 8.95 20.67 10.96 20.92C13.1 21.19 15.39 20.8 17.03 19.32C18.86 17.66 19.5 15 18.56 12.72L18.43 12.46C18.22 12 17.66 11.2 17.66 11.2M14.5 17.5C14.22 17.74 13.76 18 13.4 18.1C12.28 18.5 11.16 17.94 10.5 17.28C11.69 17 12.4 16.12 12.61 15.23C12.78 14.43 12.46 13.77 12.33 13C12.21 12.26 12.23 11.63 12.5 10.94C12.69 11.32 12.89 11.7 13.13 12C13.9 13 15.11 13.44 15.37 14.8C15.41 14.94 15.43 15.08 15.43 15.23C15.46 16.05 15.1 16.95 14.5 17.5H14.5Z';
const CURSOR_ICON_PATH = 'M13.64,21.97C13.14,22.21 12.54,22 12.31,21.5L10.13,16.76L7.62,18.78C7.45,18.92 7.24,19 7,19A1,1 0 0,1 6,18V3A1,1 0 0,1 7,2C7.24,2 7.47,2.09 7.64,2.23L7.65,2.22L19.14,11.86C19.57,12.22 19.62,12.85 19.27,13.27C19.12,13.45 18.91,13.57 18.7,13.61L15.54,14.23L17.74,18.96C18,19.46 17.76,20.05 17.26,20.28L13.64,21.97Z';

export default class InspectionAreaMarker extends BaseMarker {
    name = null;
    integrityScore = 0;
    integrityScorePercentage = 0;
    numberOfMarkers = 0;
    numberOfHotspots = 0;
    // Set in generateMesh so the viewer can re-style / re-scale them without re-traversing the group.
    lineSegments = null;
    labelSprite = null;
    fillMesh = null;       // faint additive "glass wall" shell inside the box

    constructor(inspectionArea) {
        super(inspectionArea.id, inspectionArea.type, inspectionArea.assetId, inspectionArea.scanId, inspectionArea.transform, inspectionArea.eTag);
        this.name = inspectionArea.name;
        this.integrityScore = inspectionArea.integrityScore;
        this.integrityScorePercentage = this.integrityScore * 20;
        this.numberOfMarkers = inspectionArea.numberOfMarkers;
        this.numberOfHotspots = inspectionArea.numberOfHotspots;

        this.generateMesh();
    }

    generateMesh() {
        // Fat, pickable edges. WebGL ignores LineBasicMaterial.linewidth (always 1px) and a 1px line
        // is near-impossible to click; LineSegments2 + LineMaterial render a real pixel width and
        // raycast against it (the viewer keeps material.resolution in sync with the viewport).
        //
        // Built with explicit setPositions (the proven path used by the camera-path polyline) —
        // LineSegmentsGeometry.fromEdgesGeometry did not render reliably here.
        const lineGeometry = new LineSegmentsGeometry().setPositions(buildUnitBoxEdgePositions());

        const material = new LineMaterial({
            color: ZONE_COLOR,
            linewidth: ZONE_LINE_WIDTH,
            worldUnits: false,
            transparent: true,
            opacity: 0.95,
            depthTest: false,   // stay visible through the dense point cloud (like the camera path)
            depthWrite: false,
        });
        material.resolution.set(
            typeof window !== 'undefined' ? window.innerWidth : 1,
            typeof window !== 'undefined' ? window.innerHeight : 1,
        );

        const lineSegments = new LineSegments2(lineGeometry, material);
        lineSegments.computeLineDistances();
        lineSegments.renderOrder = 998;             // drawn after the points pass
        lineSegments.userData.markerId = this.id;   // resolve back to the marker on pick
        lineSegments.userData.isZoneEdge = true;
        // markRaw: this marker instance lives in a reactive store, so any three.js object kept as a
        // field would otherwise be wrapped in a Vue proxy — and a proxied renderable throws
        // "modelViewMatrix is read-only" during render/raycast.
        this.lineSegments = markRaw(lineSegments);

        const group = new THREE.Group();
        group.add(lineSegments);

        // Glass-pane walls: a faint additive shell so the zone reads as a translucent glowing box,
        // not just a wireframe. Additive + depthWrite:false only ever brightens the points inside it
        // (never hides them). Not pickable (absent from the viewer's pick lists).
        const fillMesh = new THREE.Mesh(
            new THREE.BoxGeometry(1, 1, 1),
            new THREE.MeshBasicMaterial({
                color: ZONE_COLOR,
                transparent: true,
                opacity: ZONE_FILL_OPACITY,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                side: THREE.DoubleSide,
            }),
        );
        fillMesh.renderOrder = 997;   // under the edges (998) and label
        this.fillMesh = markRaw(fillMesh);
        group.add(fillMesh);

        group.position.set(
            this.transform.position.x,
            this.transform.position.y,
            this.transform.position.z
        );

        group.scale.set(
            this.transform.scale.x,
            this.transform.scale.y,
            this.transform.scale.z
        );

        group.rotation.set(
            this.transform.rotation.x,
            this.transform.rotation.y,
            this.transform.rotation.z
        );

        // Name label: a camera-facing sprite pinned to the top face (local +Z, the scene's up axis).
        // It's a child of the scaled group, so its scale is divided by the group scale to keep a
        // constant on-screen size regardless of the zone's dimensions.
        const label = this.buildLabelSprite();
        this.applyLabelScale(label, group.scale);
        label.position.set(0, 0, 0.5);              // top face centre in unit-box space
        label.center.set(0.5, 0);                   // anchor at the bottom of the sprite → sits above the edge
        label.userData.markerId = this.id;
        label.userData.isZoneLabel = true;
        this.labelSprite = markRaw(label);   // markRaw: see lineSegments note above
        group.add(label);

        group.visible = false;

        this.meshGroup = markRaw(group);
    }

    // Cancel the parent group's (possibly non-uniform) scale so the sprite renders at a consistent
    // world size. Called again by the viewer after a transform/resize changes the group scale.
    applyLabelScale(label, groupScale) {
        const sx = Math.abs(groupScale.x) || 1;
        const sy = Math.abs(groupScale.y) || 1;
        const sz = Math.abs(groupScale.z) || 1;
        // Height = a fraction of the box's largest dimension so the card scales with the zone, but
        // clamped to [min, max] so it stays readable on small zones and doesn't dominate large ones.
        // Dividing by sx/sy cancels the parent group's (possibly non-uniform) scale so it isn't
        // stretched.
        const worldHeight = Math.min(
            LABEL_MAX_HEIGHT,
            Math.max(LABEL_MIN_HEIGHT, Math.max(sx, sy, sz) * LABEL_SIZE_FRACTION),
        );
        const aspect = label.userData.aspect || 3;
        label.scale.set((worldHeight * aspect) / sx, worldHeight / sy, 1);
    }

    // Highlight/emphasise the zone when it's selected (brighter + thicker), or restore the default.
    setSelected(selected) {
        if (!this.lineSegments) return;
        const mat = this.lineSegments.material;
        mat.linewidth = selected ? ZONE_LINE_WIDTH_SELECTED : ZONE_LINE_WIDTH;
        mat.opacity = selected ? 1.0 : 0.9;
        mat.color.set(selected ? 0xffffff : ZONE_COLOR);

        if (this.fillMesh) {
            this.fillMesh.material.color.set(selected ? 0xffffff : ZONE_COLOR);
            this.fillMesh.material.opacity = selected ? ZONE_FILL_OPACITY_SELECTED : ZONE_FILL_OPACITY;
        }
    }

    // Rebuild the label after a rename. The sprite's texture is baked from the name at build time,
    // so mutating `this.name` alone leaves the old text on screen — re-render the card, swap the
    // texture, and re-apply the scale (the new text width changes the aspect).
    updateName(newName) {
        this.name = newName;
        this.refreshLabel();
    }

    // Re-render the label card from the marker's current fields (name, counts) and swap it onto the
    // existing sprite. Call after anything that changes what the card should show.
    refreshLabel() {
        if (!this.labelSprite) return;

        const { texture, aspect } = this.renderLabelCard();
        const oldMap = this.labelSprite.material.map;
        this.labelSprite.material.map = texture;
        this.labelSprite.material.needsUpdate = true;
        this.labelSprite.userData.aspect = aspect;
        if (oldMap) oldMap.dispose();

        this.applyLabelScale(this.labelSprite, this.meshGroup.scale);
    }

    // Snapshot the marker's current display values and render the card texture from them.
    renderLabelCard() {
        return buildLabelCard({
            name: this.name || 'Inspection Area',
            markerCount: Number(this.numberOfMarkers) || 0,
            hotspotCount: Number(this.numberOfHotspots) || 0,
        });
    }

    buildLabelSprite() {
        const { texture, aspect } = this.renderLabelCard();

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,    // stays readable inside the point cloud
            depthWrite: false,
        });
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.renderOrder = 1000;
        // Keep the canvas aspect so text isn't stretched; LABEL_HEIGHT drives the height.
        sprite.userData.aspect = aspect;
        return sprite;
    }
}

// Render the zone "card" onto a canvas and return a CanvasTexture + its aspect ratio. The card has
// three centred rows: the zone name, a stats row (marker count, hotspot count), and a
// "double-click to open" hint that tells the user how to load the zone into the sidebar.
function buildLabelCard({ name, markerCount, hotspotCount }) {
    const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    // Logical (pre-dpr) layout metrics.
    const PAD = 30;
    const TITLE_FONT = 46;
    const STAT_FONT = 40;
    const HINT_FONT = 28;
    const ICON = 42;
    const ICON_GAP = 12;   // icon → its value
    const COL_GAP = 40;    // between stat columns
    const ROW_GAP = 16;    // between rows
    const HINT_ICON = 26;
    const HINT_GAP = 9;

    const font = (size, weight = 600) => `${weight} ${size}px Inter, Arial, sans-serif`;

    // --- measure ---------------------------------------------------------------------------------
    const measure = document.createElement('canvas').getContext('2d');

    measure.font = font(TITLE_FONT, 700);
    const titleW = measure.measureText(name).width;

    measure.font = font(STAT_FONT, 600);
    const markerText = String(markerCount);
    const hotspotText = String(hotspotCount);
    const markerColW = ICON + ICON_GAP + measure.measureText(markerText).width;
    const hotspotColW = ICON + ICON_GAP + measure.measureText(hotspotText).width;
    const statsW = markerColW + COL_GAP + hotspotColW;

    measure.font = font(HINT_FONT, 500);
    const hintText = 'Double-click to open';
    const hintW = HINT_ICON + HINT_GAP + measure.measureText(hintText).width;

    const titleH = TITLE_FONT;
    const statH = Math.max(ICON, STAT_FONT);
    const hintH = Math.max(HINT_ICON, HINT_FONT);

    const contentW = Math.max(titleW, statsW, hintW);
    const w = Math.ceil(contentW + PAD * 2);
    const h = Math.ceil(PAD * 2 + titleH + ROW_GAP + statH + ROW_GAP + hintH);

    // --- draw ------------------------------------------------------------------------------------
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(w * dpr);
    canvas.height = Math.ceil(h * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // Rounded card background + mint border.
    roundRect(ctx, 1.5, 1.5, w - 3, h - 3, 24);
    ctx.fillStyle = 'rgba(16, 28, 33, 0.92)';
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(102, 199, 180, 0.95)';
    ctx.stroke();

    ctx.textBaseline = 'top';

    // Title (centred).
    let y = PAD;
    ctx.font = font(TITLE_FONT, 700);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(name, (w - titleW) / 2, y);
    y += titleH + ROW_GAP;

    // Stats row (centred as a block).
    let x = (w - statsW) / 2;
    const iconY = y + (statH - ICON) / 2;
    const textY = y + (statH - STAT_FONT) / 2;
    ctx.font = font(STAT_FONT, 600);

    drawMdiIcon(ctx, MARKER_ICON_PATH, x, iconY, ICON, '#ff6b6b');
    ctx.fillStyle = '#e6f0ee';
    ctx.fillText(markerText, x + ICON + ICON_GAP, textY);
    x += markerColW + COL_GAP;

    drawMdiIcon(ctx, HOTSPOT_ICON_PATH, x, iconY, ICON, '#ffa726');
    ctx.fillStyle = '#e6f0ee';
    ctx.fillText(hotspotText, x + ICON + ICON_GAP, textY);
    y += statH + ROW_GAP;

    // Hint row (centred): cursor-click icon + prompt.
    const hintX = (w - hintW) / 2;
    drawMdiIcon(ctx, CURSOR_ICON_PATH, hintX, y + (hintH - HINT_ICON) / 2, HINT_ICON, 'rgba(150, 185, 180, 0.9)');
    ctx.font = font(HINT_FONT, 500);
    ctx.fillStyle = 'rgba(160, 190, 185, 0.92)';
    ctx.fillText(hintText, hintX + HINT_ICON + HINT_GAP, y + (hintH - HINT_FONT) / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.needsUpdate = true;

    return { texture, aspect: w / h };
}

// Trace a rounded rectangle path (canvas roundRect isn't available everywhere).
function roundRect(ctx, x, y, w, h, r) {
    const rad = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rad, y);
    ctx.arcTo(x + w, y, x + w, y + h, rad);
    ctx.arcTo(x + w, y + h, x, y + h, rad);
    ctx.arcTo(x, y + h, x, y, rad);
    ctx.arcTo(x, y, x + w, y, rad);
    ctx.closePath();
}

// Fill an mdi 24x24 path at (x, y), scaled to `size`, in `color`.
function drawMdiIcon(ctx, pathData, x, y, size, color) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(size / 24, size / 24);
    ctx.fillStyle = color;
    ctx.fill(new Path2D(pathData));
    ctx.restore();
}

// Flat [x,y,z, x,y,z, ...] of the 12 edges (24 points) of a unit cube centred at the origin,
// for LineSegmentsGeometry.setPositions. Order: bottom face, top face, then verticals.
function buildUnitBoxEdgePositions() {
    const c = [
        [-0.5, -0.5, -0.5], [0.5, -0.5, -0.5], [0.5, 0.5, -0.5], [-0.5, 0.5, -0.5],
        [-0.5, -0.5, 0.5], [0.5, -0.5, 0.5], [0.5, 0.5, 0.5], [-0.5, 0.5, 0.5],
    ];
    const edges = [
        [0, 1], [1, 2], [2, 3], [3, 0],   // bottom face
        [4, 5], [5, 6], [6, 7], [7, 4],   // top face
        [0, 4], [1, 5], [2, 6], [3, 7],   // verticals
    ];

    const positions = [];
    for (const [i, j] of edges) {
        positions.push(c[i][0], c[i][1], c[i][2], c[j][0], c[j][1], c[j][2]);
    }
    return positions;
}
